import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChannelMap } from '@models/channel-maps/channel-map';
import { ChannelData } from '@models/meter-scans/composite/channel-data';
import { EqualizationData } from '@models/meter-scans/composite/equalization-data';
import { OfdmaData } from '@models/meter-scans/ofdma/ofdma-data';
import { MeterSysInfo } from '@models/meter-sys-info';
import { OfdmData } from '@models/ofdm-data';
import { SpectrumBin } from '@models/spectrum-bin';
import { Observable } from 'rxjs';
import { map, shareReplay, timeout } from 'rxjs/operators';
import { ObjectHelper } from '@utilities/object-helper';


@Injectable()
export class LiveMeterService {
  DEFAULT_CHANNEL_WIDTH = 6.4;
  constructor(private http: HttpClient) {
  }

  docsisDs(ip: string): Observable<ChannelData[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);

    const URL = '/live-meter-svc/rs/docsisDs';
    return this.http.get<any>(URL, { params: params })
      .pipe(
        timeout(30000),
        map((httpResponse: any) => {
          const channels = ObjectHelper.copyArray(httpResponse, ChannelData);
          channels.map((channel, index) => {
            channel.frequency = httpResponse[index]['freq'];
            channel.power = httpResponse[index]['pwr'];
          });
          return channels;
        })
      );
  }

  docsisUs(ip: string): Observable<ChannelData[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);

    const URL = '/live-meter-svc/rs/docsisUs';
    return this.http.get<any>(URL, { params: params })
      .pipe(
        timeout(30000),
        map(response => {
          return ChannelData.getChannelDataFromHttpResponse(response);
        })
      );
  }

  ofdm(ip: string): Observable<OfdmData> {
    let params = new HttpParams();
    params = params.append('ip', ip);

    const URL = '/live-meter-svc/rs/ofdm';
    return this.http.get<OfdmData>(URL, { params: params });
  }

  ofdmV2(ip: string): Observable<OfdmData> {
    let params = new HttpParams();
    params = params.append('ip', ip);

    const URL = '/live-meter-svc/rs/v2/ofdm';
    return this.http.get<any>(URL, { params: params }).pipe(
      map((response: any) => response?.data ?? response)
    );
  }

  ofdma(ip: string): Observable<OfdmaData> {
    let params = new HttpParams();
    params = params.append('ip', ip);
    const URL = '/live-meter-svc/rs/ofdma';
    return this.http.get(URL, { params }).pipe(
      map((ofdmaData) => ObjectHelper.copyObject(ofdmaData, OfdmaData))
    );
  }

  preEqualization(ip: string): Observable<EqualizationData[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);

    const URL = '/live-meter-svc/rs/preEqualization';
    return this.http.get<EqualizationData[]>(URL, { params: params })
      .pipe(
        timeout(30000),
        map((httpResponse: any) => {
          const eqDataArray = ObjectHelper.copyArray(httpResponse, EqualizationData);
          eqDataArray.map(eqData => {
            if (!eqData.bandwidth) {
              eqData.bandwidth = this.DEFAULT_CHANNEL_WIDTH;
            }
          });
          return eqDataArray;
        })
      );
  }

  preEqualizationV2(ip: string): Observable<EqualizationData[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);

    const URL = '/live-meter-svc/rs/v2/pre-equalization';
    return this.http.get<any>(URL, { params })
      .pipe(
        timeout(30000),
        map((httpResponse: any) => {
          const dataArray = Array.isArray(httpResponse)
            ? httpResponse
            : (httpResponse?.data || httpResponse?.Data || []);
          const eqDataArray = ObjectHelper.copyArray(dataArray, EqualizationData);
          eqDataArray.map(eqData => {
            if (!eqData.bandwidth) {
              eqData.bandwidth = this.DEFAULT_CHANNEL_WIDTH;
            }
          });
          return eqDataArray;
        })
      );
  }

  spectrum(ip: string, freq?: string, span?: number): Observable<SpectrumBin[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);
    if (freq) {
      params = params.append('freq', freq);
    }
    if (span) {
      params = params.append('span', span.toString());
    }

    const URL = '/live-meter-svc/rs/spectrum';
    return this.http.get<SpectrumBin[]>(URL, { params: params })
      .pipe(
        timeout(30000)
      );
  }

  getChannelScan(ip: string, frequencies: string, symbolRate: string, annex: string): Observable<ChannelData[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);
    params = params.append('annex', annex);
    params = params.append('symbolrate', symbolRate);
    params = params.append('centFreq', frequencies);

    return this.http.get('/live-meter-svc/rs/channelInfo', { params: params })
      .pipe(
        map(response => ChannelData.getChannelDataFromHttpResponse(response)),
        timeout(30000)
      );
  }

  getChannelScanV2(ip: string, centFreq: number[], annex: string): Observable<ChannelData[]> {
    const annexType = annex.toUpperCase() === 'B' ? 2 : 1;
    const params = new HttpParams({
      fromObject: {
        ip,
        centFreq: centFreq.join(','),
        annexType: annexType.toString(),
        modValue: '256'
      }
    });

    const URL = '/live-meter-svc/rs/v2/channel-info';
    return this.http.get<any>(URL, { params })
      .pipe(
        timeout(30000),
        map((response: any) => {
          const channelInfoResponse = Array.isArray(response)
            ? response
            : (response?.data || response?.Data || response?.channelInfo || response?.channels || response?.measurements || []);
          return ChannelData.getChannelDataFromHttpResponse(channelInfoResponse);
        })
      );
  }

  // TODO: move this endpoint to different service so channel-map-svc can be retired
  getGenericChannelMapByCableTelevisionFormat(name: 'HRC' | 'STD' | 'IRC'): Observable<ChannelMap> {
    const params = new HttpParams().append('name', name.toLowerCase());
    return this.http.get<any>('/channel-map-svc/channel-map/eiaChannelPlans', { params: params }).pipe(
      map(response => ChannelMap.getChannelMapFromHttpResponse(response[0])),
      shareReplay()
    );
  }

  getMeterSysInfo(ip: string): Observable<MeterSysInfo[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);
    const URL = '/live-meter-svc/rs/info';
    return this.http.get(URL, { params }).pipe(
      map((meterSysInfoData) => ObjectHelper.copyArray(meterSysInfoData, MeterSysInfo))
    );
  }

  // XM4 v2 endpoints
  getMeterSysInfoV2(ip: string): Observable<MeterSysInfo[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);
    const URL = '/live-meter-svc/rs/v2/info';
    return this.http.get(URL, { params }).pipe(
      map((meterSysInfoData) => {
        // v2 endpoint returns a single object, not an array — wrap it so copyArray can iterate
        const dataAsArray = Array.isArray(meterSysInfoData) ? meterSysInfoData : [meterSysInfoData];
        return ObjectHelper.copyArray(dataAsArray, MeterSysInfo);
      })
    );
  }

  docsisDsV2(ip: string): Observable<ChannelData[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);
    const URL = '/live-meter-svc/rs/v2/docsis-ds';
    return this.http.get<any>(URL, { params })
      .pipe(
        timeout(30000),
        map((response: any) => {
          const dsResponse = Array.isArray(response)
            ? response
            : (response?.data || response?.Data || response?.channels || []);
          return ChannelData.getChannelDataFromHttpResponse(dsResponse);
        })
      );
  }

  docsisUsV2(ip: string): Observable<ChannelData[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);
    const URL = '/live-meter-svc/rs/v2/docsis-us';
    return this.http.get<any>(URL, { params })
      .pipe(
        timeout(30000),
        map(response => {
          const usResponse = Array.isArray(response)
            ? response
            : (response?.data || response?.Data || response?.channels || []);
          return ChannelData.getChannelDataFromHttpResponse(usResponse);
        })
      );
  }

  
  spectrumV2(ip: string, freq?: string, span?: number, stepFrequency?: number, avg?: number): Observable<SpectrumBin[]> {
    let params = new HttpParams();
    params = params.append('ip', ip);
    if (freq) {
      params = params.append('freq', freq);
    }
    if (span !== undefined) {
      params = params.append('span', span.toString());
    }
    if (stepFrequency !== undefined) {
      params = params.append('stepFrequency', stepFrequency.toString());
    }
    if (avg !== undefined) {
      params = params.append('avg', avg.toString());
    }

    const URL = '/live-meter-svc/rs/v2/spectrum';
    return this.http.get<any>(URL, { params })
      .pipe(
        timeout(30000),
        map((response: any) => {
          const spectrumResponse = Array.isArray(response)
            ? response
            : (response?.data || response?.Data || response?.spectrum || response?.spectrumBins || []);
          return ObjectHelper.copyArray(spectrumResponse, SpectrumBin);
        })
      );
  }
  
}
